import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/react-hooks";
import { gql } from "apollo-boost";

import AddIcon from "@material-ui/icons/Add";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import PublicIcon from "@material-ui/icons/Public";

import ErrorPage from "commonComponents/errorPage/ErrorPage";
import Button from "designV2/components/Button";
import Field from "designV2/components/Field";
import MetricCard from "designV2/components/MetricCard";
import PageHeader from "designV2/components/PageHeader";
import Panel from "designV2/components/Panel";
import SearchField from "designV2/components/SearchField";
import { Skeleton } from "designV2/components/Skeleton";
import { readableError } from "superAdminViews/setting/SettingsList";
import "superAdminViews/setting/settingsList.css";
import "./states.css";

const GET_REGIONS = gql`
  query getRegions {
    getRegions {
      _id
      name
    }
  }
`;

const ADD_REGION = gql`
  mutation addRegion($RegionInput: [String]) {
    addRegion(RegionInput: $RegionInput)
  }
`;

/**
 * Every province and territory, not just the ten provinces the old
 * list held — the Northwest Territories, Nunavut and Yukon were simply
 * absent and could only be reached by typing them in by hand.
 */
const CANADA = [
  "alberta",
  "british columbia",
  "manitoba",
  "new brunswick",
  "newfoundland and labrador",
  "northwest territories",
  "nova scotia",
  "nunavut",
  "ontario",
  "prince edward island",
  "québec",
  "saskatchewan",
  "yukon",
];

const titleCase = (value) =>
  String(value).replace(/\b\w/g, (character) => character.toUpperCase());

/**
 * States - which provinces Spark operates in.
 *
 * `addRegion` replaces the whole set rather than adding to it, so this
 * is one form with one save, not a list with per-row actions. The old
 * screen sorted its options with `stateOptions.sort()` straight in the
 * render — mutating state during a render pass.
 */
const States = () => {
  const [selected, setSelected] = useState([]);
  const [saved, setSaved] = useState([]);
  const [extras, setExtras] = useState([]);
  const [search, setSearch] = useState("");
  const [newRegion, setNewRegion] = useState("");
  const [newRegionError, setNewRegionError] = useState("");
  const [formError, setFormError] = useState("");
  const [success, setSuccess] = useState("");
  const [saving, setSaving] = useState(false);

  const { loading, error, data } = useQuery(GET_REGIONS, { fetchPolicy: "network-only" });
  const [addRegion] = useMutation(ADD_REGION);

  useEffect(() => {
    if (!data) return;
    const active = data.getRegions.map((region) => region.name);
    setSelected(active);
    setSaved(active);
    // Anything already live that is not one of the thirteen — kept so
    // turning it off is still possible.
    setExtras(active.filter((name) => CANADA.indexOf(name) === -1));
  }, [data]);

  const options = useMemo(() => {
    const all = CANADA.concat(extras.filter((name) => CANADA.indexOf(name) === -1));
    const needle = search.trim().toLowerCase();
    return all
      .slice()
      .sort()
      .filter((name) => !needle || name.indexOf(needle) !== -1);
  }, [extras, search]);

  const dirty =
    selected.length !== saved.length ||
    selected.some((name) => saved.indexOf(name) === -1);

  const toggle = (name) => {
    setSuccess("");
    setSelected((current) =>
      current.indexOf(name) === -1
        ? current.concat(name)
        : current.filter((item) => item !== name)
    );
  };

  const onAddRegion = (event) => {
    if (event) event.preventDefault();
    const value = newRegion.trim().toLowerCase();
    if (!value) return;

    if (CANADA.indexOf(value) !== -1 || extras.indexOf(value) !== -1) {
      setNewRegionError("That region is already on the list.");
      return;
    }

    setNewRegionError("");
    setExtras((current) => current.concat(value));
    // Adding it used to leave it unticked, which meant the save that
    // followed dropped it again.
    setSelected((current) => current.concat(value));
    setNewRegion("");
  };

  const onSubmit = () => {
    if (saving) return;
    setSaving(true);
    setFormError("");
    setSuccess("");

    addRegion({ variables: { RegionInput: selected } })
      .then(() => {
        setSaving(false);
        setSaved(selected);
        setSuccess("Saved. Spark now operates in the regions ticked below.");
      })
      .catch((err) => {
        setSaving(false);
        setFormError(readableError(err));
      });
  };

  if (loading) {
    return (
      <div className="page settingsPage">
        <main className="main">
          <PageHeader title="Provinces" />
          <Skeleton height={120} />
          <Skeleton height={320} />
        </main>
      </div>
    );
  }

  if (error) return <ErrorPage />;

  return (
    <div className="page settingsPage">
      <main className="main">
        <PageHeader
          title="Provinces"
          description="Where Spark takes trips and orders. Turning a region off stops new bookings there."
          actions={
            <Button variant="primary" onClick={onSubmit} loading={saving} disabled={!dirty}>
              {saving ? "Saving" : dirty ? "Save changes" : "Saved"}
            </Button>
          }
        />

        <section className="stats">
          <MetricCard title="Active regions" value={selected.length} />
          <MetricCard title="Available" value={options.length} />
          <MetricCard
            title="Unsaved changes"
            value={dirty ? "Yes" : "No"}
            tone={dirty ? "warning" : "default"}
          />
        </section>

        {formError && (
          <div className="dsFormMessage error" role="alert">
            <ErrorOutlineIcon />
            <span>{formError}</span>
          </div>
        )}
        {success && (
          <div className="dsFormMessage success" role="status">
            <CheckCircleOutlineIcon />
            <span>{success}</span>
          </div>
        )}

        <Panel
          title="Where Spark operates"
          subtitle="Tick every province or territory that should accept bookings."
        >
          <div className="statesSearchRow">
            <SearchField
              placeholder="Search regions"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <span className="statesCount">
              {selected.length} of {options.length} on
            </span>
          </div>

          <div className="statesGrid">
            {options.map((name) => {
              const on = selected.indexOf(name) !== -1;
              return (
                <label key={name} className={`dsOptionCard ${on ? "isSelected" : ""}`}>
                  <input type="checkbox" checked={on} onChange={() => toggle(name)} />
                  <span className="dsOptionCardText">
                    <strong>{titleCase(name)}</strong>
                  </span>
                </label>
              );
            })}
          </div>

          {options.length === 0 && (
            <p className="statesEmpty">
              <PublicIcon /> No region matches that search.
            </p>
          )}
        </Panel>

        <Panel
          title="Add a region"
          subtitle="For anywhere outside the standard provinces and territories."
        >
          <form className="statesAddRow" onSubmit={onAddRegion}>
            <Field label="Region name" htmlFor="newRegion" error={newRegionError}>
              <input
                id="newRegion"
                className="dsInput"
                value={newRegion}
                placeholder="e.g. yukon"
                onChange={(event) => setNewRegion(event.target.value)}
              />
            </Field>
            <Button
              type="submit"
              variant="secondary"
              icon={<AddIcon fontSize="small" />}
              disabled={!newRegion.trim()}
            >
              Add to list
            </Button>
          </form>
        </Panel>
      </main>
    </div>
  );
};

export default States;
